import React, { useState, useEffect } from "react";
import { Modal, Alert, Checkbox, Input, Spin, message } from "antd";
import { incidentService } from "@iso/services";
import {
  INVESTIGATION_TYPES,
  INVESTIGATION_STATUSES,
  LESSONS_LEARNED_STATUS_LABELS,
} from "@iso/constants/incidentOptions";

const labelOf = (options, value) =>
  options.find((option) => option.value === value)?.label || value;

/**
 * Owner-only delete of an incident report (e.g. a duplicate). Lists what the
 * report holds, says the delete can't be undone, and only enables Delete once
 * the owner confirms the work was copied/moved and types the report number.
 */
const DeleteIncidentModal = ({ incident, onCancel, onDeleted }) => {
  const [holdings, setHoldings] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [typedNumber, setTypedNumber] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setHoldings(null);
    setAcknowledged(false);
    setTypedNumber("");
    if (!incident) return undefined;

    let cancelled = false;
    Promise.all([
      incidentService.getInvestigation(incident.id),
      incidentService.getCorrectiveActions(incident.id),
      incidentService.getLessonsLearned(incident.id),
    ]).then(([investigation, actions, lessons]) => {
      if (!cancelled) {
        setHoldings({
          investigation,
          actionCount: Array.isArray(actions) ? actions.length : 0,
          lessons,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [incident]);

  const canDelete =
    acknowledged && incident && typedNumber.trim() === incident.report_number;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await incidentService.deleteIncident(incident.id, typedNumber.trim());
      message.success(`Incident ${incident.report_number} deleted.`);
      onDeleted();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete the incident.");
    } finally {
      setDeleting(false);
    }
  };

  const investigation = holdings?.investigation;

  return (
    <Modal
      open={Boolean(incident)}
      title={`Delete Incident ${incident?.report_number || ""}`}
      onCancel={onCancel}
      onOk={handleDelete}
      confirmLoading={deleting}
      destroyOnClose
      okText="Delete permanently"
      okButtonProps={{ danger: true, disabled: !canDelete }}
    >
      <Alert
        type="error"
        showIcon
        message="This cannot be undone."
        description="Deleting removes this report and everything recorded on it: the investigation, corrective actions and lessons learned."
        style={{ marginBottom: 16 }}
      />

      <p style={{ marginBottom: 8 }}>
        <strong>This report holds:</strong>
      </p>
      {holdings ? (
        <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
          <li>
            {investigation
              ? `Investigation: ${labelOf(INVESTIGATION_TYPES, investigation.investigation_type)} (${labelOf(INVESTIGATION_STATUSES, investigation.status)})`
              : "No investigation"}
          </li>
          <li>
            {holdings.actionCount} corrective action{holdings.actionCount === 1 ? "" : "s"}
          </li>
          <li>
            {holdings.lessons
              ? `Lessons learned (${LESSONS_LEARNED_STATUS_LABELS[holdings.lessons.status] || holdings.lessons.status})`
              : "No lessons learned"}
          </li>
        </ul>
      ) : (
        <Spin size="small" style={{ display: "block", marginBottom: 16 }} />
      )}

      <p style={{ marginBottom: 16 }}>
        If this is a duplicate, make sure its approved investigation, corrective
        actions and lessons-learned draft have been copied or moved to the report
        you are keeping <strong>before</strong> you delete it.
      </p>

      <Checkbox
        checked={acknowledged}
        onChange={(e) => setAcknowledged(e.target.checked)}
        style={{ marginBottom: 16 }}
      >
        I have copied or moved the investigation, corrective actions and lessons
        learned to the report I am keeping.
      </Checkbox>

      <label htmlFor="delete-incident-confirm" style={{ display: "block", marginBottom: 4 }}>
        Type <strong>{incident?.report_number}</strong> to confirm
      </label>
      <Input
        id="delete-incident-confirm"
        value={typedNumber}
        onChange={(e) => setTypedNumber(e.target.value)}
        autoComplete="off"
      />
    </Modal>
  );
};

export default DeleteIncidentModal;
